<template>
  <div class="w-full max-h-screen bg-background flex flex-col relative">
    <div v-show="isBusy"
         class="absolute inset-0 bg-faint-white flex items-center justify-center z-10">
      <Spinner :color="'text-black'" :size="10"/>
    </div>
    <page-header
        :title="'Schedule Details'"
        :info="schedule && schedule.job_id ? schedule.job_id : ''"
        :secondary-info="getScheduleStatusLabel(schedule.status)"
        :secondary-info-color="getScheduleStatusClass(schedule.status)"
        :secondary-info-font-size="'text-sm'"
        :enableLogicalBack="true"
        back-to="schedule-list">
      <AppButton v-if="allJobSheetsCompleteOrFurtherWork"
          color="green"
          label="Email Confirmation"
          class="ml-2"
          @click="sendEmail"
          icon="mail"
      >
        <template v-slot:icon>
          <Icon iconType="mail" class="mr-2"/>
        </template>
      </AppButton>
      <button
        v-if="canCancel"
        type="button"
        @click="showCancelDialog = true"
        class="flex items-center bg-red-200 border hover:bg-red-300 border-red-400 hover:border-red-500 hover:shadow active:shadow-inner transition duration-300 text-secondary rounded-md px-2 py-1 ml-2"
      >
        <span>Cancel Schedule</span>
      </button>
    </page-header>

    <main class="pr-6 pl-6 bg-background flex-1 overflow-y-scroll">

      <!-- Schedule, Customer & Notes Summary -->
      <div class="bg-white p-5 rounded shadow-md mt-6">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <!-- Schedule Details -->
          <div class="border-2 border-orange-400 rounded-lg p-4 bg-white flex flex-col">
            <div class="flex items-center mb-3">
              <svg class="h-5 w-5 text-orange-500 mr-2" fill="none" stroke="currentColor" stroke-width="2"
                   viewBox="0 0 24 24">
                <rect x="3" y="7" width="18" height="13" rx="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M16 3h-8v4h8V3z" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <h3 class="uppercase text-lg font-semibold text-primary tracking-wide mb-0">Schedule Detail</h3>
            </div>
            <div class="mb-2">
              <label class="font-semibold text-sm">Job ID</label>
              <p class="text-md font-semibold">{{ schedule.job_id }}</p>
            </div>
            <div class="mb-2">
              <label class="font-semibold text-sm">Planned Dates</label>
              <p class="text-md font-semibold">{{ formatDate(schedule.start_date) }} — {{ formatDate(schedule.end_date) }}</p>
            </div>
            <div v-if="schedule.actual_start_date" class="mb-2">
              <label class="font-semibold text-sm">Actual Dates</label>
              <p class="text-md font-semibold">{{ formatDate(schedule.actual_start_date) }} — {{ formatDate(schedule.actual_end_date) }}</p>
            </div>
            <div class="mb-2">
              <label class="font-semibold text-sm">Site</label>
              <p class="text-md font-semibold">{{ safeDisplay(schedule.site_id) }}</p>
            </div>
            <div>
              <div class="flex flex-wrap gap-2 pt-1">
                <span v-if="schedule.is_site_visit"
                    class="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">Site Visit</span>
                <span v-if="schedule.is_pre_pay"
                    class="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">Pre-Pay</span>
                <span v-if="schedule.is_install"
                    class="px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-semibold">Install</span>
                <span v-if="schedule.requires_parts_collection"
                    class="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold">Collect Parts</span>
                <span v-if="schedule.is_confirmed"
                    class="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">Confirmed</span>
              </div>
            </div>
          </div>

          <!-- Customer Details -->
          <div class="border-2 border-orange-400 rounded-lg p-4 bg-white flex flex-col">
            <div class="flex items-center mb-3">
              <svg class="h-5 w-5 text-orange-500 mr-2" fill="none" stroke="currentColor" stroke-width="2"
                   viewBox="0 0 24 24">
                <circle cx="12" cy="8" r="4" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M6 20a6 6 0 0112 0" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <h3 class="uppercase text-lg font-semibold text-primary tracking-wide mb-0">Customer Details</h3>
            </div>
            <div class="mb-2">
              <label class="font-semibold text-sm">Customer</label>
              <p class="text-md font-semibold">{{ safeDisplay(schedule.customer && schedule.customer.name) }}</p>
            </div>
            <div class="mb-2">
              <label class="font-semibold text-sm">Job Title</label>
              <p class="text-md font-semibold">{{
                  safeDisplay(schedule.customer && schedule.customer.default_person && schedule.customer.default_person.job_title)
                }}</p>
            </div>
            <div class="mb-2">
              <label class="font-semibold text-sm">Customer Address</label>
              <p class="text-md font-semibold">
                {{ formatAddress(schedule.customer && schedule.customer.default_address) }}
              </p>
            </div>
            <div>
              <label class="font-semibold text-sm">Contact</label>
              <div class="text-md font-semibold flex flex-wrap gap-2">
                <span
                    v-if="schedule.customer && schedule.customer.default_person && schedule.customer.default_person.telephone"
                    class="mr-2">
                  {{ safeDisplay(schedule.customer.default_person.telephone) }}
                </span>
                <span
                    v-if="schedule.customer && schedule.customer.default_person && schedule.customer.default_person.mobile"
                    class="mr-2">
                  {{ safeDisplay(schedule.customer.default_person.mobile) }}
                </span>
                <span
                    v-if="schedule.customer && schedule.customer.default_person && schedule.customer.default_person.email"
                    class="mr-2">
                  {{ safeDisplay(schedule.customer.default_person.email) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Notes Column -->
          <div class="border-2 border-orange-400 rounded-lg p-4 bg-white flex flex-col">
            <div class="flex items-center mb-3">
              <svg class="h-5 w-5 text-orange-500 mr-2" fill="none" stroke="currentColor" stroke-width="2"
                   viewBox="0 0 24 24">
                <rect x="4" y="4" width="16" height="16" rx="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M8 10h8M8 14h5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <h3 class="uppercase text-lg font-semibold text-primary tracking-wide mb-0">Notes</h3>
            </div>
            <div class="mb-2 flex-1">
              <label class="font-semibold text-sm sr-only">Notes</label>
              <p
                  class="text-md font-medium whitespace-pre-wrap break-words"
                  :class="{ 'text-gray-400 italic': !schedule.notes }"
              >
                {{ schedule.notes ? schedule.notes : 'No notes available' }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Assignment Grid -->
      <div class="w-full bg-white p-5 mt-6 rounded shadow-md min-w-0 overflow-hidden">
        <div class="flex items-center mb-5 mt-1">
          <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
            Engineers & Schedule
          </h3>
          <hr class="flex-1 border-primary mx-4"/>
        </div>
        <DispatchBoard
          :value="assignmentRows"
          :startDate="schedule.start_date"
          :endDate="schedule.end_date"
          :readonly="true"
          :scheduleId="schedule.schedule_id"
        />
      </div>

      <!-- Equipment Details -->
      <div class="w-full bg-white p-5 mt-6 rounded shadow-md">
        <div class="flex items-center mb-5 mt-1">
          <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
            Equipment Details
          </h3>
          <hr class="flex-1 border-primary mx-4"/>
        </div>

        <div v-for="(item, index) in (schedule.equipment || [])"
             :key="index"
             class="border border-gray rounded-3xl mb-4">
          <div class="flex flex-wrap justify-between items-center mb-3 bg-grayF7F7F7 rounded-t-3xl p-4 py-6">
            <div>
              <h4 class="text-lg font-bold flex items-center gap-2">
                {{ safeDisplay(item.name) }}
                <span
                    class="mx-2 text-xs font-semibold px-2 py-1 rounded"
                    :class="getEquipmentStatusClass(item.job_sheet)">
                  {{ getEquipmentStatusBadge(item.job_sheet) }}
                </span>
              </h4>
              <p class="text-xs text-black">
                <span class="font-light text-mediumGray">Product Code:</span>
                {{ safeDisplay(item.product_code) }}
              </p>
            </div>

            <div class="flex justify-end items-center space-x-2">
              <button
                  v-if="!item.job_sheet"
                  class="w-20 px-3 py-1 rounded bg-blue-500 text-white text-xs font-semibold"
                  @click="handleCreateJobSheet(item)">
                Create
              </button>
              <button
                  v-else
                  class="w-20 px-3 py-1 rounded text-xs font-semibold"
                  :class="{
                    'bg-green-500 text-white': item.job_sheet && (item.job_sheet.job_satisfaction === true || item.job_sheet.job_satisfaction === 'yes'),
                    'bg-red-500 text-white': item.job_sheet && (item.job_sheet.job_satisfaction === false || item.job_sheet.job_satisfaction === 'no' || item.job_sheet.job_satisfaction === 'incomplete'),
                    'bg-yellow-500 text-white': item.job_sheet && !item.job_sheet.job_satisfaction
                  }"
                  @click="handleViewJobSheet(item)">
                View
              </button>

              <button
                  v-if="item.follow_up_id || canCreateFollowUp(item)"
                  class="px-3 py-1 rounded bg-orange-500 text-white text-xs font-semibold"
                  @click="openFollowUp(item)">
                {{ item.follow_up_id ? 'Edit follow-up' : 'Create follow-up' }}
              </button>
            </div>
          </div>
          <div class="flex flex-col lg:flex-row flex-wrap p-4">
            <div class="flex-1 pb-4">
              <label class="text-sm font-semibold text-mediumGray">Make:</label>
              <p class="text-md font-semibold">{{ safeDisplay(item.make) }}</p>
            </div>
            <div class="flex-1 pb-4">
              <label class="text-sm font-semibold text-mediumGray">Serial Number:</label>
              <p class="text-md font-semibold">{{ safeDisplay(item.serial_number) }}</p>
            </div>
            <div class="flex-1 pb-4">
              <label class="text-sm font-semibold text-mediumGray">Product Type:</label>
              <p class="text-md font-semibold">{{ safeDisplay(item.product_type) }}</p>
            </div>
            <div class="flex-1 pb-4">
              <label class="text-sm font-semibold text-mediumGray">Job Types:</label>
              <div class="flex flex-wrap gap-2 pt-1">
                <span v-if="item.is_service"
                      class="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">Service</span>
                <span v-if="item.is_calibration"
                      class="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">Calibration</span>
                <span v-if="item.is_repair"
                      class="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold">Repair</span>
                <span v-if="item.is_install"
                      class="px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-semibold">Install</span>
                <span v-if="item.is_warranty"
                      class="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold">Warranty</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Schedule Completion Section -->
      <div v-if="allJobSheetsCompleteOrFurtherWork" class="w-full bg-white mt-6 mb-6 p-5 pt-1 rounded shadow-md">
        <div class="mt-6">
          <div class="flex items-center mb-5">
            <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
              Schedule completion
            </h3>
            <hr class="flex-1 border-primary ml-4"/>
            <button
                @click="saveSchedule"
                type="button"
                class="ml-4 flex items-center bg-blue-200 border hover:bg-blue-300 border-blue-400 hover:border-blue-500 hover:shadow active:shadow-inner transition duration-300 text-secondary rounded-md px-4 py-2"
            >
              <svg
                  class="stroke-current h-5 w-5 mr-2"
                  fill="none"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  viewBox="0 0 24 24"
              >
                <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/>
                <path d="M17 21v-8H7v8M7 3v5h8"/>
              </svg>
              <span>Save</span>
            </button>
          </div>

          <div class="flex flex-col md:flex-row w-full">
            <div class="w-full md:w-4/12 p-2 mb-4 md:mb-0">
              <label class="uppercase font-semibold text-sm">Work completed to my satisfaction</label>
              <Dropdown
                  class="w-full bge-input rounded"
                  v-model="selectedScheduleCompletionAction"
                  :options="scheduleCompletionActionList"
                  optionLabel="name"
                  placeholder="<Action>"
              />
            </div>

            <div class="w-full md:w-3/12 p-2 mb-4 md:mb-0">
              <label class="uppercase font-semibold text-sm">Customer Name</label>
              <input
                  class="w-full bge-input bge-input-spacing rounded"
                  v-model="schedule.signing_customer_name"
                  type="text"
              />
            </div>

            <div v-if="schedule && schedule.schedule_id" class="w-full md:w-5/12 p-2">
              <label class="uppercase font-semibold text-sm mb-1 block">Customer Signature</label>
              <div class="w-full">
                <Signature
                    :uploadRefId="schedule.schedule_id"
                    :uploadSignatureF="uploadScheduleSignature"
                    :getSignatureF="getScheduleSignature"
                    class="w-full bge-input bge-input-spacing rounded"
                />
              </div>
            </div>

            <div v-else class="w-full md:w-3/12 p-2 mb-4 md:mb-0">
              <label class="uppercase font-semibold text-sm">Order No.</label>
              <input
                  class="w-full bge-input bge-input-spacing rounded"
                  v-model="schedule.order_no"
                  type="text"
              />
            </div>
          </div>
        </div>
      </div>
      <!-- Schedule Completion Warning Section -->
      <div v-else class="w-full bg-white mt-6 mb-6 p-5 pt-1 rounded shadow-md">
        <div class="mt-6">
          <div class="flex items-center mb-5">
            <svg class="h-5 w-5 text-yellow-500 mr-2" fill="none" stroke="currentColor" stroke-width="2"
                 viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M12 8v4m0 4h.01" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <h3 class="uppercase text-lg font-semibold text-yellow-700 tracking-wide mb-0">
              Schedule Completion
            </h3>
            <hr class="flex-1 border-yellow-300 ml-4"/>
          </div>
          <div class="flex flex-col md:flex-row w-full">
            <div class="w-full p-2">
              <div class="bg-yellow-50 border border-yellow-300 text-yellow-800 rounded-md flex items-center px-4 py-4">
                <svg class="w-6 h-6 mr-3 text-yellow-400 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M12 8v4m0 4h.01" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span class="text-base font-semibold">
                  Schedule completion can only be set when
                  <span class="font-bold text-black">all equipment job sheets</span>
                  are
                  <span class="text-green-700 font-bold">Completed</span>
                  or
                  <span class="text-orange-600 font-bold">Completed With Further Work</span>.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Job Invoice Status Section (Admin Only) -->
      <div v-if="allJobSheetsCompleteOrFurtherWork" class="w-full bg-white mt-6 mb-6 p-5 pt-1 rounded shadow-md">
        <div class="mt-6">
          <div class="flex items-center mb-5">
            <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
              Job Invoice Status
            </h3>
            <hr class="flex-1 border-primary ml-4"/>
            <button
                    @click="saveSchedule"
                    type="button"
                    class="ml-4 flex items-center bg-blue-200 border hover:bg-blue-300 border-blue-400 hover:border-blue-500 hover:shadow active:shadow-inner transition duration-300 text-secondary rounded-md px-4 py-2">
              <svg class="stroke-current h-5 w-5 mr-2"
                   fill="none"
                   stroke-width="2"
                   stroke-linecap="round"
                   stroke-linejoin="round"
                   viewBox="0 0 24 24">
                <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/>
                <path d="M17 21v-8H7v8M7 3v5h8"/>
              </svg>
              <span>Save</span>
            </button>
          </div>
          <div class="flex flex-col md:flex-row w-full">
            <div class="w-full md:w-4/12 p-2 mb-4 md:mb-0">
              <label class="uppercase font-semibold text-sm">Status</label>
              <Dropdown
                  class="w-full bge-input rounded"
                  v-model="selectedScheduleStatus"
                  :options="scheduleStatusList"
                  optionLabel="name"
                  placeholder="Select Status"
              />
            </div>
            <div class="w-full md:w-3/12 p-2 mb-4 md:mb-0">
              <label class="uppercase font-semibold text-sm">Invoice Number</label>
              <input
                  class="w-full bge-input bge-input-spacing rounded"
                  v-model="schedule.invoice_number"
                  type="text"
              />
            </div>
          </div>
        </div>
      </div>

    </main>

    <CancelDialog
      :visible="showCancelDialog"
      :scheduleId="scheduleId"
      @cancelled="onCancelled"
      @close="showCancelDialog = false"
    />

    <portal to="overlay-outlet">
      <panel
          :showing="sendEmailPanelOpen"
          @close="sendEmailPanelOpen = false"
          title="Send Email"
      >
        <SendEmail v-if="schedule"
                   :scheduleId="schedule.schedule_id"
                   :jobId="schedule.job_id || schedule.job_id"
                   :customer="schedule.customer"
                   :schedule="schedule"
        />
      </panel>
    </portal>

    <portal to="overlay-outlet">
      <panel
          :showing="followUpPanelOpen"
          @close="followUpPanelOpen = false"
          title="Follow-up"
      >
        <ScheduleFollowUpPanel
            v-if="schedule && followUpTargetItem"
            :schedule="schedule"
            :equipment="followUpTargetItem"
            :followUpId="followUpId"
            @close="followUpPanelOpen = false"
            @saved="loadScheduleData"
        />
      </panel>
    </portal>
  </div>
</template>

<script>
import { mapState } from "vuex";
import moment from "moment";
import PageHeader from "@/components/PageHeader.vue";
import Spinner from "@/components/Spinner.vue";
import Signature from "@/components/Signature.vue";
import Panel from "@/components/Panel.vue";
import AppButton from "@/components/Button.vue";
import Icon from "@/components/Icon.vue";
import Dropdown from "primevue/dropdown";
import SendEmail from "@/views/HR/Engineer/MySchedule/Components/SendEmail.vue";
import ScheduleFollowUpPanel from "@/views/HR/Engineer/MySchedule/Components/ScheduleFollowUpPanel.vue";
import DispatchBoard from "./Components/DispatchBoard.vue";
import StatusBadge from "./Components/StatusBadge.vue";
import CancelDialog from "./Components/CancelDialog.vue";
import notificationMixin from "@/mixins/notificationMixin";

export default {
  name: "ScheduleDetail",
  mixins: [notificationMixin],
  components: {
    PageHeader, Spinner, Signature, Panel, AppButton, Icon, Dropdown,
    SendEmail, ScheduleFollowUpPanel, DispatchBoard, StatusBadge, CancelDialog,
  },
  props: {
    scheduleId: { type: String, required: true },
  },
  data() {
    return {
      isBusy: false,
      schedule: {},
      showCancelDialog: false,
      sendEmailPanelOpen: false,
      followUpPanelOpen: false,
      followUpTargetItem: null,
      followUpId: null,
      selectedScheduleCompletionAction: null,
      scheduleCompletionActionList: [
        { id: "yes", name: "YES – JOB COMPLETED" },
        { id: "no", name: "NO – JOB COMPLETED" },
        { id: "incomplete", name: "JOB INCOMPLETE – FURTHER WORK REQUIRED" },
      ],
      selectedScheduleStatus: null,
      scheduleStatusList: [
        { id: "ReadyForInvoice", name: "Ready for Invoice" },
        { id: "Invoiced", name: "Invoiced" },
        { id: "Closed", name: "Closed" },
      ],
    };
  },
  computed: {
    ...mapState("schedule", ["activeSchedule", "assignments", "summary"]),
    schedule() {
      return this.activeSchedule || {};
    },
    assignmentRows() {
      if (!this.assignments || !this.assignments.length) return [];
      return this.assignments
          .filter(a => a.status !== 'Cancelled')
          .map(a => ({
            engineer_id: a.engineer_id,
            engineer_name: a.engineer ? `${a.engineer.first_name || ''} ${a.engineer.last_name || ''}`.trim() : a.engineer_id,
            engineer_email: a.engineer ? a.engineer.email || '' : '',
            scheduled_date: a.scheduled_date,
            start_time: a.start_time || null,
            end_time: a.end_time || null,
            status: a.status || 'Assigned',
            assignment_id: a.assignment_id || null,
          }));
    },
    canCancel() {
      return this.schedule && !["Invoiced", "Closed", "Cancelled"].includes(this.schedule.status);
    },
    canEditAssignments() {
      return this.schedule && ["NotStarted", "InProgress"].includes(this.schedule.status);
    },
    allJobSheetsCompleteOrFurtherWork() {
      if (!this.schedule || !this.schedule.equipment || this.schedule.equipment.length === 0) {
        return false;
      }
      return this.schedule.equipment.every(
          eq =>
              eq.job_sheet &&
              (eq.job_sheet.status === 'Completed' || eq.job_sheet.status === 'CompletedWithFurtherWork')
      );
    },
  },
  async mounted() {
    if (this.scheduleId) {
      await this.loadSchedule();
    }
  },
  watch: {
    scheduleId() {
      this.loadSchedule();
    },
  },
  methods: {
    async loadSchedule() {
      this.isBusy = true;
      try {
        await this.$store.dispatch("schedule/fetchScheduleById", this.scheduleId);
        await this.$store.dispatch("schedule/fetchAssignments", this.scheduleId);
        await this.$store.dispatch("schedule/fetchSummary", this.scheduleId);
        this.initCompletionForm();
      } catch (error) {
        console.error("Error loading schedule:", error);
      } finally {
        this.isBusy = false;
      }
    },
    initCompletionForm() {
      if (!this.schedule) return;

      if (this.schedule.completion_status) {
        this.selectedScheduleCompletionAction = this.scheduleCompletionActionList.find(
            (action) => action.id === this.schedule.completion_status
        ) || null;
      } else {
        this.selectedScheduleCompletionAction = null;
      }

      if (this.schedule.status) {
        this.selectedScheduleStatus = this.scheduleStatusList.find(
            (status) => status.id === this.schedule.status
        ) || null;
      } else {
        this.selectedScheduleStatus = null;
      }
    },
    formatDate(val) {
      if (!val) return "No data";
      return moment.unix(val).format("DD-MM-YYYY");
    },
    formatAddress(addr) {
      if (!addr) return "";
      return [addr.address1, addr.address2, addr.city, addr.postcode].filter(Boolean).join(", ");
    },
    safeDisplay(val, fallback = "No data") {
      return val && val !== "" ? val : fallback;
    },

    // ── Schedule status helpers ──
    getScheduleStatusLabel(status) {
      switch (status) {
        case "Draft": return "Draft";
        case "NotStarted": return "Not Started";
        case "InProgress": return "In Progress";
        case "Completed": return "Completed";
        case "CompletedWithFurtherWork": return "Completed (Further Work)";
        case "Cancelled": return "Cancelled";
        case "ReadyForInvoice": return "Ready for Invoice";
        case "Invoiced": return "Invoiced";
        case "Closed": return "Closed";
        default: return status || "Unknown";
      }
    },
    getScheduleStatusClass(status) {
      switch (status) {
        case "Draft": return "bg-gray-400 text-white";
        case "NotStarted": return "bg-blue-400 text-white";
        case "InProgress": return "bg-blue-500 text-white";
        case "Completed": return "bg-green-500 text-white";
        case "CompletedWithFurtherWork": return "bg-red-500 text-white";
        case "Cancelled": return "bg-gray-400 text-white";
        case "ReadyForInvoice": return "bg-purple-700 text-white";
        case "Invoiced": return "bg-gray-500 text-white";
        case "Closed": return "bg-black text-white";
        default: return "bg-gray-200 text-gray-700";
      }
    },

    // ── Equipment status helpers ──
    getEquipmentStatusBadge(jobSheet) {
      if (!jobSheet) return "Not Started";
      if (jobSheet.job_satisfaction === true || jobSheet.job_satisfaction === "yes") return "Completed (Yes)";
      if (jobSheet.job_satisfaction === false || jobSheet.job_satisfaction === "no") return "Completed (No)";
      if (jobSheet.job_satisfaction === "incomplete") return "Incomplete";
      return "In Progress";
    },
    getEquipmentStatusClass(jobSheet) {
      if (!jobSheet) return 'bg-gray-200 text-gray-700';
      if (jobSheet.job_satisfaction === true || jobSheet.job_satisfaction === 'yes')
        return 'bg-green-500 text-white';
      if (jobSheet.job_satisfaction === false || jobSheet.job_satisfaction === 'no' || jobSheet.job_satisfaction === 'incomplete')
        return 'bg-red-500 text-white';
      return 'bg-yellow-500 text-white';
    },

    isReadyForInvoiceAllowed() {
      if (!this.schedule || !Array.isArray(this.schedule.equipment) || this.schedule.equipment.length === 0) {
        return false;
      }
      return this.schedule.equipment.every(eq => {
        if (!eq.job_sheet) return false;
        if (eq.job_sheet.status === 'Completed') return true;
        if (eq.job_sheet.status === 'CompletedWithFurtherWork') {
          return !!eq.follow_up_id;
        }
        return false;
      });
    },

    canCreateFollowUp(item) {
      if (!item || !item.job_sheet) return false;
      const js = item.job_sheet;
      return (
          js.job_satisfaction === 'incomplete' ||
          js.job_satisfaction === 'no' ||
          js.job_satisfaction === false
      );
    },

    openFollowUp(item) {
      this.followUpTargetItem = item || null;
      if (item && item.follow_up_id) {
        this.followUpId = item.follow_up_id;
      } else {
        this.followUpId = null;
      }
      this.followUpPanelOpen = true;
    },

    handleCreateJobSheet(item) {
      const jobTypeMap = [
        { key: 'is_service', value: 'S' },
        { key: 'is_calibration', value: 'C' },
        { key: 'is_repair', value: 'R' },
        { key: 'is_install', value: 'I' },
        { key: 'is_warranty', value: 'W' },
      ];
      const matchedType = jobTypeMap.find(type => item[type.key]);
      const jobType = matchedType ? matchedType.value : '';

      this.$router.push({
        name: "engineer-job-sheet-create",
        query: {
          job_id: this.schedule.job_id,
          equipment_id: item.equipment_id,
          product_code: item.product_code,
          job_type: jobType,
          schedule_id: this.schedule.schedule_id,
          schedule_id: this.schedule.schedule_id,
        },
      });
    },

    handleViewJobSheet(item) {
      this.$router.push({
        name: "engineer-job-sheet-edit",
        params: { job_sheet_id: item.job_sheet.job_sheet_id },
      });
    },

    sendEmail() {
      this.sendEmailPanelOpen = true;
    },

    async uploadScheduleSignature(files, refId) {
      return this.EquipmentScheduleService.uploadDocuments(files, refId);
    },
    async getScheduleSignature(refId, category) {
      return this.EquipmentScheduleService.getScheduleDocuments(refId, category);
    },

    async saveSchedule() {
      this.isBusy = true;
      try {
        if (this.selectedScheduleStatus && this.selectedScheduleStatus.id) {
          const targetStatus = this.selectedScheduleStatus.id;

          if (targetStatus === 'ReadyForInvoice' && !this.isReadyForInvoiceAllowed()) {
            this.notifyError(
                'You can only set status to "Ready for Invoice" when all equipment job sheets are Completed and any "Completed with Further Work" items have a follow-up.'
            );
            this.isBusy = false;
            return;
          }

          if (['Invoiced', 'Closed'].includes(targetStatus) && this.schedule.status !== 'ReadyForInvoice') {
            this.notifyError('Please set the status to "Ready for Invoice" before marking as Invoiced or Closed.');
            this.isBusy = false;
            return;
          }
        }

        const formattedSchedule = this.getAllScheduleCompletionData(this.schedule);

        if (this.schedule.schedule_id) {
          await this.EquipmentScheduleService.patchScheduleCompletion(
              this.schedule.schedule_id,
              formattedSchedule,
          );
          this.notifySuccess("Schedule updated successfully");
        }

        await this.loadScheduleData();
        // Re-sync schedule status after schedule completion update
        await this.$store.dispatch("schedule/fetchScheduleById", this.scheduleId);
      } catch (error) {
        const backendError = (error && error.response && error.response.data)
            ? error.response.data
            : (error && error.data)
                ? error.data
                : null;

        if (backendError && Array.isArray(backendError.message)) {
          const formattedMsg = this.formatValidationMessage(backendError.message[0]);
          this.notifyError(formattedMsg);
        } else {
          const fallbackMsg = backendError && backendError.message
              ? backendError.message
              : "Couldn't update schedule";
          this.notifyError(fallbackMsg);
        }
        console.error(backendError && backendError.message ? backendError.message : error);
      } finally {
        this.isBusy = false;
      }
    },

    getAllScheduleCompletionData(schedule) {
      let completion_status = null;
      let signing_customer_name = '';
      let status = null;
      let invoice_number = null;

      if (this.selectedScheduleCompletionAction && this.selectedScheduleCompletionAction.id) {
        completion_status = this.selectedScheduleCompletionAction.id;
      } else if (schedule.completion_status) {
        completion_status = schedule.completion_status;
      }

      signing_customer_name = (schedule.signing_customer_name || '').trim();
      invoice_number = (schedule.invoice_number || '').trim();

      if (this.selectedScheduleStatus && this.selectedScheduleStatus.id) {
        status = this.selectedScheduleStatus.id;
      } else if (schedule.status) {
        status = schedule.status;
      }

      return { completion_status, signing_customer_name, status, invoice_number };
    },

    formatValidationMessage(msg) {
      if (typeof msg === 'object' && msg.message) return msg.message;
      return String(msg);
    },

    async onCancelled() {
      this.showCancelDialog = false;
      this.notifySuccess("Schedule cancelled.");
      await this.loadSchedule();
    },
  },
};
</script>

<style scoped>
.bg-grayF7F7F7 {
  background-color: #F7F7F7;
}
</style>
